import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { api } from "@/lib/api";
import { Sun, Moon, MagnifyingGlass, Bell, SignOut } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { timeAgo } from "@/lib/api";

const CRUMBS = {
  "/dashboard": ["Home", "Dashboard"],
  "/my-queue": ["Home", "My Queue"],
  "/cases": ["Home", "Cases"],
  "/cases/new": ["Home", "Cases", "New"],
  "/queue": ["Home", "Queue Board"],
  "/allocation": ["Home", "Allocation Engine"],
  "/workforce": ["Home", "Workforce"],
  "/exceptions": ["Home", "Exceptions"],
  "/reports": ["Home", "Reports"],
  "/analytics": ["Home", "Analytics"],
  "/notifications": ["Home", "Notifications"],
  "/admin": ["Home", "Administration"],
  "/profile": ["Home", "Profile"],
};

export default function TopBar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const loc = useLocation();
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (alive) setNotifs(data);
      } catch {}
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const crumbs = CRUMBS[loc.pathname] || (loc.pathname.startsWith("/cases/") ? ["Home", "Cases", "Detail"] : ["Home"]);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/cases?search=${encodeURIComponent(q.trim())}`);
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch {}
  };

  return (
    <header className="sticky top-0 z-20 border-b bg-background/85 backdrop-blur-xl">
      <div className="h-16 px-6 flex items-center gap-4">
        <nav className="flex items-center gap-2 text-xs text-muted-foreground mono" data-testid="breadcrumbs">
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-2">
              {i > 0 && <span className="opacity-50">/</span>}
              <span className={i === crumbs.length - 1 ? "text-foreground" : ""}>{c}</span>
            </span>
          ))}
        </nav>
        <form onSubmit={onSearch} className="flex-1 max-w-md ml-auto relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search cases, customers, IDs…"
            className="pl-9 h-9 bg-secondary/50 border-transparent focus-visible:bg-background"
          />
        </form>

        <Button data-testid="theme-toggle-btn" variant="ghost" size="icon" onClick={toggle} className="rounded-md">
          {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
        </Button>

        <Popover>
          <PopoverTrigger asChild>
            <button
              data-testid="notifications-btn"
              className="relative rounded-md h-9 w-9 grid place-items-center hover:bg-secondary transition-colors"
            >
              <Bell size={18} />
              {notifs.unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold grid place-items-center">
                  {notifs.unread}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-96 p-0">
            <div className="p-3 border-b flex items-center justify-between">
              <div className="text-sm font-semibold">Notifications</div>
              <button data-testid="notif-mark-all-btn" onClick={markAll} className="text-xs text-primary hover:underline">Mark all read</button>
            </div>
            <div className="max-h-96 overflow-auto">
              {notifs.items.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No notifications</div>}
              {notifs.items.map(n => (
                <div key={n.id} className={`p-3 border-b hover:bg-secondary/40 cursor-pointer ${!n.read ? "bg-primary/5" : ""}`} onClick={() => n.link && nav(n.link)}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{n.title}</div>
                      <div className="text-xs text-muted-foreground truncate">{n.body}</div>
                    </div>
                    {!n.read && <Badge variant="default" className="h-2 w-2 p-0 rounded-full" />}
                  </div>
                  <div className="text-[10px] mono text-muted-foreground mt-1">{timeAgo(n.at)}</div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <Button data-testid="logout-btn" variant="ghost" size="icon" onClick={logout}>
          <SignOut size={18} />
        </Button>
      </div>
    </header>
  );
}
